/**
 * LivechatAdoption — Admin panel view of which merchants have activated
 * the storefront chatbot (Livechat), with conversation activity metrics.
 *
 * CSS prefix: la- (Livechat Adoption) — see CLAUDE.md on prefix conventions.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MessageCircle,
  Search,
  RefreshCw,
  Download,
  CheckCircle2,
  XCircle,
  Users,
  Activity,
  Mail,
  Bell,
  Inbox,
  ExternalLink,
  Copy,
  ChevronDown,
} from 'lucide-react';
import './LivechatAdoption.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const TIERS = ['FREE', 'PRO', 'PREMIUM', 'ENTERPRISE'];

const STATUS_TABS = [
  { value: 'all', label: 'All' },
  { value: 'enabled', label: 'Activated' },
  { value: 'disabled', label: 'Not activated' },
];

const SORT_OPTIONS = [
  { value: 'lastActivity', label: 'Last activity' },
  { value: 'conversations', label: 'Conversations' },
  { value: 'storeName', label: 'Store name' },
];

// Derive the merchant's live storefront URL from the platform domain.
// Mirrors how the merchant frontend builds storefront URLs.
const getStorefrontUrl = (slug) => {
  if (!slug) return null;
  const platform = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com';
  return `https://${slug}.${platform}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatRelative = (iso) => {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Never';
  const diffMs = Date.now() - t;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const StatCard = ({ icon: Icon, label, value, sublabel }) => (
  <div className="la-stat-card">
    <div className="la-stat-icon" aria-hidden="true">
      <Icon size={18} />
    </div>
    <div className="la-stat-body">
      <div className="la-stat-value">{value}</div>
      <div className="la-stat-label">{label}</div>
      {sublabel ? <div className="la-stat-sub">{sublabel}</div> : null}
    </div>
  </div>
);

const TierBadge = ({ tier }) => (
  <span className={`la-tier la-tier-${(tier || 'FREE').toLowerCase()}`}>{tier || 'FREE'}</span>
);

const StatusBadge = ({ enabled }) => (
  <span className={`la-status ${enabled ? 'la-status-on' : 'la-status-off'}`}>
    {enabled ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
    <span>{enabled ? 'Activated' : 'Not activated'}</span>
  </span>
);

const LivechatAdoption = () => {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [summary, setSummary] = useState({
    totalStores: 0,
    enabledCount: 0,
    totalConversations: 0,
    activeLast7d: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('');
  const [sort, setSort] = useState('lastActivity');
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '25',
        status: statusFilter,
        sort,
        ...(tierFilter ? { tier: tierFilter } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      });
      const res = await fetch(`${API_BASE}/api/admin/storefront-chatbot/adoption?${params}`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || `Request failed (${res.status})`);
      }
      setRows(Array.isArray(data.data) ? data.data : []);
      setPagination(data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
      setSummary(
        data.summary || { totalStores: 0, enabledCount: 0, totalConversations: 0, activeLast7d: 0 },
      );
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, tierFilter, sort, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, tierFilter, sort]);

  const adoptionRate = useMemo(() => {
    if (!summary.totalStores) return '0%';
    return `${Math.round((summary.enabledCount / summary.totalStores) * 100)}%`;
  }, [summary]);

  const handleCopyEmail = useCallback(async (email) => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
    } catch {
      // Clipboard API can fail in non-secure contexts — silent fallback.
    }
  }, []);

  const handleExport = () => {
    if (rows.length === 0) return;
    const headers = [
      'Store',
      'Slug',
      'Merchant',
      'Tier',
      'Livechat',
      'Conversations',
      'Last activity',
      'Bot name',
      'Fallback to human',
    ];
    const lines = [
      headers.join(','),
      ...rows.map((r) =>
        [
          JSON.stringify(r.storeName || ''),
          JSON.stringify(r.storeSlug || ''),
          JSON.stringify(r.merchantEmail || ''),
          r.merchantTier || 'FREE',
          r.isEnabled ? 'ON' : 'OFF',
          r.conversationCount,
          r.lastConversationAt ? new Date(r.lastConversationAt).toISOString() : '',
          JSON.stringify(r.botDisplayName || ''),
          r.fallbackToHuman === null ? '' : r.fallbackToHuman ? 'Yes' : 'No',
        ].join(','),
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `livechat-adoption-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="la-page">
      <header className="la-header">
        <div className="la-header-text">
          <h1 className="la-title">
            <MessageCircle size={22} />
            <span>Livechat adoption</span>
          </h1>
          <p className="la-subtitle">
            Which merchants have activated the storefront chatbot and how it's being used.
          </p>
        </div>
        <div className="la-header-actions">
          <button type="button" className="la-btn la-btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'la-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            className="la-btn la-btn-secondary"
            onClick={handleExport}
            disabled={rows.length === 0}
          >
            <Download size={14} />
            <span>Export CSV</span>
          </button>
        </div>
      </header>

      <section className="la-stats">
        <StatCard
          icon={Users}
          label="Total stores"
          value={summary.totalStores.toLocaleString()}
          sublabel="In the platform"
        />
        <StatCard
          icon={CheckCircle2}
          label="Livechat activated"
          value={summary.enabledCount.toLocaleString()}
          sublabel={`${adoptionRate} adoption`}
        />
        <StatCard
          icon={Inbox}
          label="Total conversations"
          value={summary.totalConversations.toLocaleString()}
          sublabel="All time"
        />
        <StatCard
          icon={Activity}
          label="Active last 7 days"
          value={summary.activeLast7d.toLocaleString()}
          sublabel={summary.totalConversations
            ? `${Math.round((summary.activeLast7d / summary.totalConversations) * 100)}% of all`
            : '—'}
        />
      </section>

      <section className="la-toolbar">
        <div className="la-search">
          <Search size={14} className="la-search-icon" aria-hidden="true" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by store, slug, or merchant email"
            className="la-search-input"
          />
        </div>

        <div className="la-filters">
          {/* Segmented pill control for activation status — per CLAUDE.md
              pill/toggle style is required for all tab switches. */}
          <div className="la-segment" role="tablist" aria-label="Filter by activation status">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={statusFilter === tab.value}
                className={`la-segment-tab ${statusFilter === tab.value ? 'is-active' : ''}`}
                onClick={() => setStatusFilter(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <label className="la-field">
            <span className="la-field-label">Tier</span>
            <div className="la-field-select">
              <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
                <option value="">All</option>
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} aria-hidden="true" />
            </div>
          </label>

          <label className="la-field">
            <span className="la-field-label">Sort</span>
            <div className="la-field-select">
              <select value={sort} onChange={(e) => setSort(e.target.value)}>
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} aria-hidden="true" />
            </div>
          </label>
        </div>
      </section>

      {error ? (
        <div className="la-error">
          <XCircle size={14} />
          <span>{error}</span>
        </div>
      ) : null}

      <section className="la-table-wrap">
        <table className="la-table">
          <thead>
            <tr>
              <th>Store / Merchant</th>
              <th>Tier</th>
              <th>Livechat</th>
              <th className="la-num">Conversations</th>
              <th>Last activity</th>
              <th>Settings</th>
              <th className="la-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="la-table-empty">
                  <RefreshCw size={16} className="la-spin" /> Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="la-table-empty">
                  No stores match the current filters.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.storeId}>
                  <td>
                    <div className="la-cell-name">
                      <strong>{r.storeName || r.storeSlug || 'Untitled store'}</strong>
                      <span className="la-cell-sub">
                        {r.merchantEmail || r.merchantUsername || '—'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <TierBadge tier={r.merchantTier} />
                  </td>
                  <td>
                    <StatusBadge enabled={r.isEnabled} />
                  </td>
                  <td className="la-num">
                    {r.conversationCount > 0 ? (
                      <span className="la-num-strong">{r.conversationCount.toLocaleString()}</span>
                    ) : (
                      <span className="la-num-zero">0</span>
                    )}
                  </td>
                  <td>
                    <div className="la-cell-name">
                      <span>{formatRelative(r.lastConversationAt)}</span>
                      {r.lastConversationAt ? (
                        <span className="la-cell-sub">{formatDate(r.lastConversationAt)}</span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <div className="la-flags">
                      {r.fallbackToHuman ? (
                        <span className="la-flag" title="Falls back to human merchant">
                          <Users size={11} /> Handoff
                        </span>
                      ) : null}
                      {r.emailMerchantOnFirstMessage ? (
                        <span className="la-flag" title="Emails merchant on first message">
                          <Mail size={11} /> Email
                        </span>
                      ) : null}
                      {r.pushMerchantOnFirstMessage ? (
                        <span className="la-flag" title="Pushes merchant on first message">
                          <Bell size={11} /> Push
                        </span>
                      ) : null}
                      {!r.hasSettings ? (
                        <span className="la-cell-sub">No settings yet</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="la-actions-col">
                    <div className="la-actions">
                      {r.storeSlug ? (
                        <a
                          className="la-icon-btn"
                          href={getStorefrontUrl(r.storeSlug)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open storefront in a new tab"
                          aria-label="Open storefront"
                        >
                          <ExternalLink size={13} />
                        </a>
                      ) : null}
                      {r.merchantEmail ? (
                        <button
                          type="button"
                          className="la-icon-btn"
                          onClick={() => handleCopyEmail(r.merchantEmail)}
                          title={`Copy merchant email (${r.merchantEmail})`}
                          aria-label="Copy merchant email"
                        >
                          <Copy size={13} />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {pagination.pages > 1 ? (
        <footer className="la-pager">
          <button
            type="button"
            className="la-btn la-btn-secondary"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="la-pager-text">
            Page {pagination.page} of {pagination.pages} · {pagination.total.toLocaleString()} stores
          </span>
          <button
            type="button"
            className="la-btn la-btn-secondary"
            disabled={page >= pagination.pages || loading}
            onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
          >
            Next
          </button>
        </footer>
      ) : null}
    </div>
  );
};

export default LivechatAdoption;
